'use client';

import { useState, useEffect, useCallback } from 'react';
import { CONTENT_CONTAINER_SELECTOR, DEBOUNCE_MS } from './constants';
import { debounce } from '@/utils/debounce';

const HYDRATION_DELAY_MS = 100; // extra delay on mobile to wait for React hydration to complete

export interface Heading {
  id: string;
  text: string;
  level: number;
  element: Element;
}

interface UseHeadingsOptions {
  minHeadingLevel: number;
  maxHeadingLevel: number;
  isMobile: boolean;
}

// builds a CSS selector for heading tags within the specified range (e.g., "h2, h3, h4")
const buildHeadingSelector = (min: number, max: number): string =>
  Array.from({ length: max - min + 1 }, (_, i) => `h${min + i}`).join(', ');

// generates a URL-safe slug from text (e.g., "Hello World!" → "hello-world")
const generateSlug = (text: string): string => {
  const lowercase = text.toLowerCase();
  const hyphenated = lowercase.replace(/[^a-z0-9]+/g, '-'); // replace non-alphanumeric runs with hyphens
  const trimmed = hyphenated.replace(/^-|-$/g, ''); // remove leading/trailing hyphens
  return trimmed;
};

// ensures an element has a unique ID, appending a counter suffix if needed
const ensureUniqueId = (el: Element, baseId: string): string => {
  let candidateId = baseId;
  let counter = 1;

  // keep incrementing until we find an ID that's either unused or already belongs to this element
  while (document.getElementById(candidateId) && document.getElementById(candidateId) !== el) {
    candidateId = `${baseId}-${counter++}`;
  }

  return candidateId;
};

// checks if any added node is or contains a heading element
const isHeadingNode = (node: Node, selector: string): boolean => {
  if (node.nodeType !== Node.ELEMENT_NODE) return false;
  const el = node as Element;
  return el.matches?.(selector) || !!el.querySelector?.(selector);
};

// checks if a mutation contains relevant heading changes
const hasRelevantHeadingChanges = (mutations: MutationRecord[], selector: string): boolean =>
  mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => isHeadingNode(node, selector)));

// compares two heading arrays to detect meaningful changes (ignores element reference changes)
const headingsHaveChanged = (prev: Heading[], next: Heading[]): boolean => {
  if (prev.length !== next.length) return true;
  return prev.some((heading, i) => heading.id !== next[i]?.id || heading.text !== next[i]?.text);
};

// checks if an element is nested within a specific ancestor tag (e.g., <footer>)
const isNestedWithin = (element: Element, ancestorTag: string): boolean => element.closest(ancestorTag) !== null;

/**
 * Extracts heading elements from the page content for use in a table of contents.
 * Automatically watches for DOM changes and updates when new headings appear.
 */
export const useHeadings = ({ minHeadingLevel, maxHeadingLevel, isMobile }: UseHeadingsOptions) => {
  const [headings, setHeadings] = useState<Heading[]>([]);

  const headingSelector = buildHeadingSelector(minHeadingLevel, maxHeadingLevel);

  // collect headings from content containers only (not header, footer, cookie banners, etc.)
  const collectHeadings = useCallback(() => {
    const allContainers = document.querySelectorAll(CONTENT_CONTAINER_SELECTOR);

    // exclude containers nested within <footer> (e.g., footer links, copyright sections)
    const containers = Array.from(allContainers).filter((container) => !isNestedWithin(container, 'footer'));

    const collected: Heading[] = [];

    containers.forEach((container) => {
      const elements = container.querySelectorAll(headingSelector);
      elements.forEach((el) => {
        // avoid duplicates if same heading appears in nested containers
        if (collected.some((h) => h.element === el)) return;

        const text = el.textContent?.trim() || '';
        if (!text) return;

        // use existing ID or generate one
        // NOTE: We set IDs on headings that don't have them to enable scroll-to-section via URL hash.
        // This is a common pattern for Table of Contents components. The ideal solution would be
        // for the content source to provide IDs, but this fallback handles dynamic/CMS content.
        let id = el.id;
        if (!id) {
          const baseId = generateSlug(text);
          id = ensureUniqueId(el, baseId);
          el.id = id;
        }

        collected.push({
          id,
          text,
          level: parseInt(el.tagName[1], 10), // extract number from tagName (e.g., "H2" → 2)
          element: el,
        });
      });
    });

    // only update state if headings actually changed (prevents unnecessary re-renders)
    setHeadings((prev) => (headingsHaveChanged(prev, collected) ? collected : prev));
  }, [headingSelector]);

  // collect headings and watch for DOM changes
  useEffect(() => {
    const debouncedCollectHeadings = debounce(collectHeadings, DEBOUNCE_MS);

    // initial collection
    collectHeadings();

    // mobile: also collect after a short delay to handle hydration timing
    const hydrationTimeoutId = isMobile ? setTimeout(collectHeadings, HYDRATION_DELAY_MS) : null;

    // watch for new headings (dynamic content from CMS, etc.)
    const observer = new MutationObserver((mutations) => {
      if (hasRelevantHeadingChanges(mutations, headingSelector)) {
        debouncedCollectHeadings();
      }
    });

    // observe entire document for added elements (childList: direct children, subtree: all descendants)
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (hydrationTimeoutId) clearTimeout(hydrationTimeoutId);
      observer.disconnect();
    };
  }, [isMobile, headingSelector, collectHeadings]);

  return headings;
};
